/**
 * ai.js — Rule-based opponent for Sky-Drop's "vs AI" mode.
 *
 * Pure decision logic: given the AI player, the human player, and the current
 * game state, it returns the same input shape the human produces from the
 * keyboard, which the caller feeds straight into handlePlayerInput(). Keeping
 * this free of React refs and rendering makes the opponent's behaviour easy to
 * read and tune.
 *
 * Difficulty comes from gameState.difficulty and maps to a profile of decision
 * parameters below — reaction speed (per-frame chance gates), beam precision,
 * kick aggression, and whether the AI dodges.
 */

import {
  screenWidth,
  BODY_TOP,
  BODY_BOTTOM,
  BODY_HALF_WIDTH,
  GROUND_Y,
  MUZZLE_DY,
  BOARD_MOUNT_RANGE,
  CELL_DOOR_TOP_Y,
  CELL_DOOR_BOTTOM_Y,
  CELL_DOOR_HEADROOM,
  fitsThroughCellDoor,
} from './constants';
import { raycastX, isBlockedAhead } from './obstacles';
import { MOVES, FINISH_RANGE, isGuarding } from './melee';
import { aiRandom } from './simulation';

/**
 * Difficulty profiles. Chance gates are per-frame probabilities at ~60fps, so
 * e.g. 0.04 ≈ one trigger per 0.4s on average — that's the AI's "reaction time".
 *
 *  fireChance    how quickly it takes a lined-up beam shot
 *  switchChance  how quickly it commits to switching board modes
 *  fireMargin    how much clearance inside the target's lethal band the muzzle
 *                needs before it shoots. Negative values fire on shots that
 *                miss by that margin, so easy sprays and wastes its 6s
 *                recharge; hard waits until the shot is unambiguously lethal
 *  kickRange     distance under which it kicks the board (aggression)
 *  dodges        whether it breaks beam alignment / evades kicked boards
 *  meleeChance   how quickly it throws a punch or kick that is already in
 *                range — the hand-to-hand equivalent of fireChance
 *  sweepChance   share of its openings spent on a sweep instead of damage.
 *                The sweep barely hurts; it buys the knockdown that the
 *                finishers cash in, so this is really "how much does it play
 *                for the setup rather than the hit"
 *  retreatHp     melee HP at or below which it disengages and goes for its
 *                board instead of trading. 0 means it never breaks off
 *  guardChance   how often it covers up on seeing a swing start. Reactive, so
 *                it only ever blocks moves slow enough to react to — easy
 *                never blocks at all
 *  lobChance     how readily it throws a cluster shell or the EMP when it has
 *                the range for one. Low numbers by design even at the top:
 *                ordnance is an arc with a fuse, so an AI that threw on every
 *                opening would just be a machine gun of grenades
 */
const PROFILES = {
  // A punching bag, and deliberately so — the tier below what used to be the
  // bottom of the scale. It barely picks the beam up, and when it does it fires
  // at shots that miss by more than a body's half-height, so it spends most of
  // the match on a wasted 6s recharge. Kicks only from arm's length, swings
  // about once a second, and never blocks or dodges. Somebody who has never
  // played this should be able to beat it.
  easy: {
    fireChance: 0.004, switchChance: 0.003, fireMargin: -38, kickRange: 95,
    dodges: false, meleeChance: 0.008, sweepChance: 0, retreatHp: 0,
    guardChance: 0, lobChance: 0.0015,
  },
  // Was 'easy'. Takes a lined-up shot inside a second, still can't dodge and
  // still wastes shots that miss by up to a third of a body.
  medium: {
    fireChance: 0.015, switchChance: 0.01, fireMargin: -20, kickRange: 140,
    dodges: false, meleeChance: 0.02, sweepChance: 0, retreatHp: 0,
    guardChance: 0, lobChance: 0.004,
  },
  // Was 'medium'. The first tier that fights back: it breaks your firing line,
  // plays for the knockdown, covers up, and disengages when it is losing a
  // hand-to-hand trade.
  hard: {
    fireChance: 0.04, switchChance: 0.02, fireMargin: -4, kickRange: 170,
    dodges: true, meleeChance: 0.06, sweepChance: 0.25, retreatHp: 3,
    guardChance: 0.10, lobChance: 0.010,
  },
  // Was 'hard', pushed a little past it so the top of the scale is a new tier
  // rather than a relabelled one: it only shoots when the shot is unambiguously
  // lethal, reaches further for the board kick, and breaks off melee at half
  // health rather than fighting on into a loss.
  xhard: {
    fireChance: 0.11, switchChance: 0.06, fireMargin: 4, kickRange: 210,
    dodges: true, meleeChance: 0.17, sweepChance: 0.4, retreatHp: 6,
    guardChance: 0.32, lobChance: 0.018,
  },
};

/**
 * The difficulty tiers, in ascending order — the single source of truth for how
 * many there are, what they are keyed by, and what they are called.
 *
 * `id` keys PROFILES, is what lives in game state, and is what the score bucket
 * is named after; `label` is what the selector button says. One table so the
 * menu, the setup line and the record cannot drift apart from the profiles.
 *
 * The whole scale shifted down one place on 2026-08-04: what was easy is now
 * medium, medium is hard, hard is x-hard, and a genuinely gentle easy was added
 * beneath them. Any pre-existing "vs AI" record was earned against a different
 * opponent than its name now implies, which is why score.js buckets these
 * separately rather than pooling them.
 */
export const DIFFICULTIES = [
  { id: 'easy', label: 'Easy' },
  { id: 'medium', label: 'Medium' },
  { id: 'hard', label: 'Hard' },
  { id: 'xhard', label: 'X-Hard' },
];

/** @param {string} id @returns {boolean} whether id names a difficulty tier. */
export const isDifficulty = (id) => DIFFICULTIES.some((d) => d.id === id);

// Distance beyond which the AI prefers sniping with the beam over closing in.
const BEAM_DISTANCE = 400;

/** @returns {object} a neutral, all-false input frame. */
function noInput() {
  return {
    left: false,
    right: false,
    jump: false,
    down: false,
    action: false,
    beam: false,
    switchMode: false,
    punch: false,
    kick: false,
    cluster: false,
    emp: false,
    guard: false,
  };
}

/**
 * Centre-to-centre distance at which a move connects.
 *
 * Derived from the move table rather than written out again here: these numbers
 * decide whether the AI steps into range or swings at air, so a reach retuned
 * in melee.js has to move the AI with it.
 *
 * @param {string} move Key into MOVES.
 * @returns {number} distance in px.
 */
function rangeOf(move) {
  return BODY_HALF_WIDTH * 2 + MOVES[move].reach;
}

/**
 * Compute the AI opponent's input for the current frame.
 *
 * @param {object} aiPlayer    The AI-controlled player (always player2).
 * @param {object} humanPlayer The human player to react to (player1).
 * @param {object} gameState   Full game state (advancement intent, difficulty).
 * @returns {object} Input frame: {left, right, jump, down, action, beam, switchMode}.
 */
export function getAIInput(aiPlayer, humanPlayer, gameState) {
  // A stunned player can't act; don't waste decisions.
  if (aiPlayer.stunned > 0) {
    return noInput();
  }

  const P = PROFILES[gameState.difficulty] || PROFILES.medium;
  const aiInput = noInput();
  // Every chance check rolls on the game's own AI stream, never Math.random,
  // so a seeded vs-AI game replays exactly.
  const roll = () => aiRandom(gameState);

  // How much room this screen actually has. The outer screens are more than
  // twice the landing stage, so an AI holding a constant edge in mind would
  // stop closing (and stop keeping its distance) two thirds of the way across
  // the big rooms and stand there.
  const roomW = screenWidth(gameState.currentScreen);

  // Its board may simply be gone — an EMP kills one outright for half a minute,
  // and for that whole time there is nothing to walk back to. Without this the
  // recovery branch below would send it to stand over a dead deck pressing the
  // pickup button until the timer ran out.
  const boardDead = aiPlayer.boardDown > 0;

  // Ordnance is thrown in feet mode, and the pulse is saved for a target that
  // is actually riding something — spending the one EMP on somebody already on
  // foot is spending it on nothing.
  const wantEmp = aiPlayer.empAmmo > 0 && humanPlayer.onBoard
    && humanPlayer.boardDown <= 0;

  // Relative geometry. Canvas y grows downward.
  const distanceToHuman = Math.abs(aiPlayer.x - humanPlayer.x);
  const aiIsLeft = aiPlayer.x < humanPlayer.x;

  // --- Aim -----------------------------------------------------------------
  // A beam leaves the cannon at MUZZLE_DY above the shooter's feet and kills
  // anything whose body box that line crosses. So aiming is about putting the
  // muzzle line inside the target's band, NOT about matching heights: the
  // shooter's muzzle naturally sits 24px above the target's body centre when
  // both stand on the floor, so a "centre the shot" rule would demand standing
  // below the ground and could never be satisfied in the most common position
  // in the game.
  const muzzleY = aiPlayer.y + MUZZLE_DY;
  const targetTop = humanPlayer.y + BODY_TOP;
  const targetBottom = humanPlayer.y + BODY_BOTTOM;
  // Margin to the nearest edge of the lethal band: positive means the shot
  // already connects, negative is how far outside the band it passes.
  const aimClearance = Math.min(muzzleY - targetTop, targetBottom - muzzleY);
  const aimTooHigh = muzzleY < targetTop; // shot sails over them — descend
  const aimTooLow = muzzleY > targetBottom; // shot passes under them — rise

  // Cover check. Obstacles stop beams, so a shot that is perfectly lined up can
  // still be wasted on a slab — and with a 6s recharge, wasting one is close to
  // losing the exchange. Treat a blocked lane as "not aligned" so the AI keeps
  // repositioning instead of firing into concrete.
  const aimTowardHuman = Math.sign(humanPlayer.x - aiPlayer.x) || 1;
  const beamReach = raycastX(gameState.currentScreen, aiPlayer.x, muzzleY, aimTowardHuman);
  const shotIsClear =
    aimTowardHuman > 0 ? beamReach >= humanPlayer.x : beamReach <= humanPlayer.x;
  const aligned = aimClearance > P.fireMargin && shotIsClear;

  // --- Threat --------------------------------------------------------------
  // The same geometry from the other side: where the human's shot would cross
  // our own body.
  const humanMuzzleY = humanPlayer.y + MUZZLE_DY;
  const myTop = aiPlayer.y + BODY_TOP;
  const myBottom = aiPlayer.y + BODY_BOTTOM;
  const threatClearance = Math.min(humanMuzzleY - myTop, myBottom - humanMuzzleY);

  // Escaping means moving the body clear of that line. Take whichever way out
  // is shorter — but never pick a direction the floor blocks, which is what
  // pins a grounded AI holding "down" against the deck while it gets shot.
  const riseDistance = aiPlayer.y - (humanMuzzleY - BODY_BOTTOM) + 2;
  const dropDistance = humanMuzzleY - BODY_TOP - aiPlayer.y + 2;
  const canDrop = aiPlayer.y + dropDistance <= GROUND_Y;
  const dodgeUp = !canDrop || riseDistance <= dropDistance;

  // Advancement intent — the AI (Red) advances left when it holds the kill.
  const canAdvance =
    gameState.advancementUnlocked && gameState.advancingPlayer === 'player2';

  // Threat reads (used by the dodge behaviours on medium/hard).
  const humanCanBeam =
    humanPlayer.onBoard &&
    humanPlayer.boardMode === 'hands' &&
    humanPlayer.beamRecharge <= 0;
  const beamThreat = P.dodges && humanCanBeam && threatClearance > -20;
  const incomingBoard = P.dodges && isBoardIncoming(aiPlayer, humanPlayer);

  // A shot already in its windup. This is the telegraph the beam charge exists
  // for, and it outranks everything else the AI might be doing: the shot is
  // committed, so breaking the firing line is guaranteed to beat it. Easy
  // reacts to it too (unlike the speculative beamThreat above) — otherwise the
  // tell would only ever be readable by the human.
  const incomingBeam = humanPlayer.beamCharge > 0 && threatClearance > -12;

  // Is a shot available at all? No point winding one up at a target that is
  // still spawn-protected, or without a charged beam.
  const canFire =
    aiPlayer.beamRecharge <= 0 &&
    aiPlayer.beamCharge <= 0 &&
    humanPlayer.invuln <= 0;

  // Whether to commit to the sniping *plan* — give up mobility for the cannon.
  // Range only gates the decision to pick the beam up: once it is already in
  // hand, any lethal shot is worth taking (see canFire). Re-testing the range
  // at the moment of firing meant that closing even slightly while lining up
  // silently cancelled the plan, and the AI would drift into kicking range
  // having never taken the shot it spent the approach setting up.
  const wantBeam = canFire && distanceToHuman > BEAM_DISTANCE;

  // Facing is only changed by movement input, so steer just enough to turn
  // around — holding a direction to "face" someone also walks at them, which
  // is what dragged the AI out of sniping range in the first place.
  const facingHuman = aiPlayer.facingRight === aiIsLeft;

  // A committed windup of our own overrides all decisions: movement is locked
  // to a crawl anyway and the shot fires itself, so emit a neutral frame
  // rather than flailing at controls that are ignored.
  if (aiPlayer.beamCharge > 0) {
    return aiInput;
  }

  if (aiPlayer.onBoard && aiPlayer.boardMode === 'hands') {
    // === HANDS MODE: slow, but can fire the beam ===
    if (incomingBeam) {
      // A shot is already winding up at our height — get out of the line.
      aiInput.jump = dodgeUp;
      aiInput.down = !dodgeUp;
    } else if (beamThreat) {
      // Human is lined up to beam us first — break alignment immediately.
      aiInput.jump = dodgeUp;
      aiInput.down = !dodgeUp;
    } else if (canFire && (aligned || !facingHuman)) {
      // Turn to face the target if needed, then fire on the reaction gate.
      // Facing is applied before the beam within the same input frame.
      if (!facingHuman) {
        aiInput.right = aiIsLeft;
        aiInput.left = !aiIsLeft;
      }
      if (aligned && facingHuman && roll() < P.fireChance) {
        aiInput.beam = true;
      }
    } else if (roll() < P.switchChance) {
      // Beam spent, or the shot won't line up from here — Hands mode can
      // barely move vertically, so drop back to feet and re-aim with the
      // hoverboard's full mobility. Self-clears: next frame boardMode is 'feet'.
      aiInput.switchMode = true;
    }
  } else if (aiPlayer.onBoard) {
    // === FEET MODE: full mobility, no beam ===

    if (incomingBeam) {
      // Priority 0: a shot is committed and aimed at our body. Nothing else
      // matters for the next few frames — leave the firing line.
      aiInput.jump = dodgeUp;
      aiInput.down = !dodgeUp;
    } else if (incomingBoard) {
      // Priority 1: a kicked board is flying at us — hop over it.
      aiInput.jump = true;
    } else if (beamThreat && !canAdvance) {
      // Priority 2: break the human's beam alignment (survival beats position).
      aiInput.jump = dodgeUp;
      aiInput.down = !dodgeUp;
    } else if (canAdvance && aiPlayer.x > 5) {
      // Priority 3: holding the kill — push left toward the goal, all the way
      // to the wall (screen advancement triggers at x <= 10; stopping short at
      // x ≈ 50, as the original logic did, meant the AI could never advance).
      aiInput.left = true;
      if (aiPlayer.x < 140 && !fitsThroughCellDoor(aiPlayer.y)) {
        // The wall only opens at its portal, floating at mid-height.
        // Arriving off-band means pressing against glass forever, so steer
        // for the portal's centre — this outranks the anti-snipe weave,
        // which would hold the AI off the band and stall the advance it is
        // trying to make. (On foot, jump still works: a full jump peaks
        // inside the band.)
        const bandMid =
          (CELL_DOOR_TOP_Y + CELL_DOOR_HEADROOM + CELL_DOOR_BOTTOM_Y) / 2;
        aiInput.jump = aiPlayer.y > bandMid; // below the portal — rise
        aiInput.down = aiPlayer.y < bandMid; // above the portal — sink
      } else if (beamThreat) {
        // Weave vertically while advancing so the human can't line up a snipe.
        aiInput.jump = dodgeUp;
        aiInput.down = !dodgeUp;
      }
    } else if (wantEmp && roll() < P.lobChance) {
      // Priority 3.4: the pulse. No range test and no facing test, because it
      // fills the arena — the only question it asks is whether there is a board
      // out there worth killing, and wantEmp is that question.
      aiInput.emp = true;
    } else if (aiPlayer.clusterAmmo > 0 && facingHuman && distanceToHuman > 200
               && distanceToHuman < 600 && roll() < P.lobChance) {
      // Priority 3.5: lob a shell. Only at a range the arc actually covers —
      // close up it lands behind them, at full room length it falls short.
      aiInput.cluster = true;
    } else if (wantBeam && aligned) {
      // Priority 4: far away with a charged beam — switch to hands to snipe.
      // (Beams only fire in hands mode. Condition self-clears once boardMode
      // flips.)
      if (roll() < P.switchChance) {
        aiInput.switchMode = true;
      }
    } else if (wantBeam) {
      // Priority 5: find a firing position.
      if (!shotIsClear) {
        // Cover in the lane. Barriers are short, so climbing opens the shot —
        // and without this the AI would sit perfectly aimed at a wall, since
        // the height check alone sees nothing wrong with its position.
        aiInput.jump = true;
      } else {
        aiInput.jump = aimTooLow; // shot would pass under them — rise
        aiInput.down = aimTooHigh; // shot would sail over them — descend
      }
    } else if (distanceToHuman > P.kickRange) {
      // Priority 6: close the gap toward the human.
      if (aiIsLeft && aiPlayer.x < roomW - 100) {
        aiInput.right = true;
      } else if (!aiIsLeft && aiPlayer.x > 100) {
        aiInput.left = true;
      }

      // Match the human's height on the way in. The board flies level from the
      // feet, so this lines up feet with feet rather than muzzle with body.
      const heightDiff = aiPlayer.y - humanPlayer.y;
      if (heightDiff > 50) {
        aiInput.jump = true;
      } else if (heightDiff < -50) {
        aiInput.down = true;
      }
    } else {
      // Priority 7: in kick range — but only actually kick when the board will
      // travel into them. It launches level with the feet, so a kick from well
      // above or below just throws the board away for nothing. Kicking blindly
      // every frame also self-perpetuates: each kick hops the rider upward, it
      // remounts, kicks again, and pogos all the way to the ceiling while the
      // boards sail harmlessly overhead.
      const boardWillConnect =
        aiPlayer.y > humanPlayer.y + BODY_TOP &&
        aiPlayer.y < humanPlayer.y + BODY_BOTTOM;

      if (!facingHuman) {
        aiInput.right = aiIsLeft;
        aiInput.left = !aiIsLeft;
      } else if (boardWillConnect) {
        aiInput.action = true;
      } else {
        // Get level with them first.
        aiInput.jump = aiPlayer.y >= humanPlayer.y + BODY_BOTTOM;
        aiInput.down = aiPlayer.y <= humanPlayer.y + BODY_TOP;
      }
    }
  } else if (boardDead && humanPlayer.onBoard) {
    // === BOARD DEAD, THEIRS ISN'T: there is nothing to recover ===
    // A rider will kill anyone on foot with a thrown board, so standing still
    // is the worst option. Keep moving, keep away, and take the ground if the
    // advance is ours — an EMP'd fighter can still walk through a portal, since
    // a full jump peaks inside the band.
    if (canAdvance && aiPlayer.x > 5) {
      aiInput.left = true;
      if (aiPlayer.x < 140 && !fitsThroughCellDoor(aiPlayer.y)) {
        aiInput.jump = true;
      }
    } else if (distanceToHuman < 220) {
      aiInput.left = aiIsLeft;
      aiInput.right = !aiIsLeft;
      if (aiPlayer.grounded) aiInput.jump = true; // break the firing line
    } else {
      aiInput.left = aiIsLeft;
      aiInput.right = !aiIsLeft;
    }
  } else if (!humanPlayer.onBoard && distanceToHuman < 200
             && aiPlayer.hp > P.retreatHp) {
    // === BOTH ON FOOT, BOTH DISARMED: hand-to-hand ===
    // Contact used to decide this outright, so the only correct play was to
    // charge. Now there is a fight to have, and the AI has to pick moves.
    //
    // Deliberately still loses the trade to a human who spaces well: it swings
    // on a per-frame probability rather than on a read of the opponent's
    // windup, so its timing is reactive-at-best by construction. Difficulty
    // buys it speed and a setup game, not clairvoyance.
    const swinging = aiPlayer.attack !== '';
    const canAct = aiPlayer.downTime <= 0 && aiPlayer.hurtTime <= 0 && !swinging;
    const grounded = !!aiPlayer.grounded;
    // Two reads that drive the guard game: whether they are covering up (so a
    // normal attack would be wasted) and whether a swing of theirs is in the
    // air (so covering up is worth doing).
    const humanIsGuarding = isGuarding(humanPlayer);
    const humanIsWindingUp = humanPlayer.attack !== '' && !humanPlayer.attackHit;

    if (!canAct) {
      // Committed or recovering — the frame is already spent.
    } else if (!grounded) {
      // Airborne, and the only attack that exists up here is the dive. Thrown
      // eagerly: having left the ground the AI is committed to landing
      // somewhere anyway, so a dive costs it nothing it has not already spent.
      aiInput.kick = roll() < P.meleeChance * 3;
    } else if (!facingHuman) {
      // Turn first. Every strike projects forward from the body, so a swing
      // with your back to them is simply thrown away.
      aiInput.right = aiIsLeft;
      aiInput.left = !aiIsLeft;
    } else if (humanPlayer.downTime > 0) {
      // They are on the floor. Close the last step, then finish: the stomp
      // hits harder, so the only reason to punch here is that they are getting
      // up and the shorter move still lands.
      if (distanceToHuman > FINISH_RANGE - 6) {
        aiInput.right = aiIsLeft;
        aiInput.left = !aiIsLeft;
      } else if (roll() < P.meleeChance * 2) {
        aiInput.kick = humanPlayer.downTime > MOVES.stomp.time;
        aiInput.punch = !aiInput.kick;
      }
    } else if (distanceToHuman <= rangeOf('punch')) {
      // Inside everything.
      if (roll() < P.meleeChance) {
        // A guard stops punches and kicks outright, so throwing one into it is
        // strictly wasted. Sweeping is the answer, and the AI has to take it or
        // a human can simply hold block and never be hit.
        const mustGoLow = humanIsGuarding
          || (roll() < P.sweepChance && grounded);
        if (mustGoLow && grounded) {
          aiInput.guard = true; // the guard stance is what makes a kick a sweep
          aiInput.kick = true;
        } else {
          // Kick when there is room to recover from a whiff, punch when there
          // is not: the kick hits twice as hard but is committed for half again
          // as long, which is a bad trade at point-blank range.
          const roomy = distanceToHuman > rangeOf('punch') * 0.7;
          aiInput.kick = roomy;
          aiInput.punch = !roomy;
        }
      } else if (P.guardChance && humanIsWindingUp && roll() < P.guardChance) {
        // They have committed to a swing and it has not landed yet — cover up.
        aiInput.guard = true;
      }
    } else if (distanceToHuman <= rangeOf('kick')) {
      // Kick range only.
      if (roll() < P.meleeChance) {
        // A standing kick dies on a guard, so leave the ground instead and come
        // down over the top of it. Only from out here — from point-blank the
        // dive overshoots them entirely.
        if (humanIsGuarding && P.guardChance) {
          aiInput.jump = true;
        } else {
          aiInput.kick = true;
        }
      } else {
        aiInput.right = aiIsLeft;
        aiInput.left = !aiIsLeft;
      }
    } else {
      // Out of reach — close, and get level so the strike bands overlap.
      aiInput.right = aiIsLeft;
      aiInput.left = !aiIsLeft;
      const heightDiff = aiPlayer.y - humanPlayer.y;
      if (heightDiff < -20 && grounded) aiInput.jump = true;
    }
  } else {
    // === DISMOUNTED: recover the board, stay away from the human ===

    const board = aiPlayer.board;
    const dx = board.x - aiPlayer.x;
    const dy = board.y - aiPlayer.y;
    const distanceToOwnBoard = Math.hypot(dx, dy);

    // A board still travelling under its own momentum is a live projectile.
    // Recalling on distance alone yanked it back about six frames after every
    // kick — always before it could cross the gap — so the AI could throw the
    // board endlessly without it ever reaching anyone.
    const boardInFlight = !board.returning && Math.hypot(board.vx, board.vy) > 6;

    // Walking only helps if there is a horizontal gap to walk across. With the
    // board directly below a ledge the AI is standing on, stepping either way
    // achieves nothing and there is no control for climbing down.
    const walkable = Math.abs(dx) > 10;
    const boardAbove = dy < -30;

    // Every case below ends in an input. The bug this replaces was a missing
    // one: a board closer than the walk threshold but not yet mounted matched
    // no branch at all, so an AI that had walked up to its own board stood
    // beside it doing nothing until the match timed out. It was waiting for a
    // pickup "on contact" that does not exist — mounting is an explicit action
    // for the AI exactly as it is for a human.
    if (boardDead) {
      // Nothing to go back to yet; the branch above handles the rest.
    } else if (boardInFlight) {
      // Let the throw finish; just don't stand somewhere lethal meanwhile.
    } else if (distanceToOwnBoard < BOARD_MOUNT_RANGE) {
      aiInput.action = true; // step back onto it
    } else if (!board.returning && (distanceToOwnBoard > 100 || (!walkable && !boardAbove))) {
      // Too far to walk to, or separated vertically with no way down: recall it
      // and let it come to us.
      aiInput.action = true;
    } else {
      if (dx > 10) aiInput.right = true;
      else if (dx < -10) aiInput.left = true;
      if (boardAbove) aiInput.jump = true;
    }

    // While dismounted we're one board-hit from death — keep distance.
    if (distanceToHuman < 80) {
      if (aiIsLeft && aiPlayer.x > 50) {
        aiInput.left = true; // Move away
      } else if (!aiIsLeft && aiPlayer.x < roomW - 50) {
        aiInput.right = true; // Move away
      }
    }
  }

  // Terrain fix-up. Every decision above is made in terms of the opponent, not
  // the arena, so rather than thread obstacle awareness through each branch,
  // correct the outcome: if we just decided to walk into something, climb it
  // instead. Without this a barrier in the lane pins the AI against it holding
  // a direction that can never make progress.
  if (aiInput.left || aiInput.right) {
    const heading = aiInput.right ? 1 : -1;
    if (isBlockedAhead(gameState.currentScreen, aiPlayer.x, aiPlayer.y, heading)) {
      aiInput.jump = true;
      aiInput.down = false;
    }
  }

  return aiInput;
}

/**
 * Is the human's kicked board flying toward the AI on a collision course?
 * True only while the board is loose (human dismounted), moving fast, closing
 * horizontally, and near the AI's height.
 */
function isBoardIncoming(aiPlayer, humanPlayer) {
  const board = humanPlayer.board;
  if (humanPlayer.onBoard) return false; // board is under the human, not flying
  if (Math.abs(board.vx) < 6) return false; // too slow to be a threat

  const dx = aiPlayer.x - board.x;
  const closing = Math.sign(dx) === Math.sign(board.vx); // heading our way
  return closing && Math.abs(dx) < 260 && Math.abs(board.y - aiPlayer.y) < 50;
}
